const editorFeedback = {
  saveConflict:
    "This content changed in another tab or session. Your draft is still here. Copy your changes, then reload the page to review the latest version before saving again.",
  requestFailed:
    "The request could not be completed. Your draft is still here. Check your connection and try again.",
  validationFailed: "Fix the fields below before saving.",
  saved: "Saved to database.",
};

const curriculumTopicCount = (count: number) =>
  `${count} ${count === 1 ? "Topic" : "Topics"}`;

export const strings = {
  storyBuilder: {
    authored: "Авторська активність Interclass",
    focus: "Мовленнєві завдання",
    prompts: "Запитання вчителя",
    example: "Приклад мовлення",
    guidance: "Як працювати з історією",
    outcome: "Очікуваний результат:",
  },
  appName: "Interclass",
  metadata: {
    title: "Interclass Platform",
    description: "Clickable PoC for the Interclass teacher experience.",
  },
  design: {
    guidanceDescription:
      "Semantic guidance uses a label, icon, border, and subtle tint so color is never the only signal.",
    guidanceTitle: "System guidance",
    subtitle: "The product tokens and component treatments used by Interclass.",
    title: "Design system",
    examples: {
      commonMistakeBody: "Avoid replacing discovery with a long explanation.",
      commonMistakeTitle: "Explaining too early",
      mainTakeawayBody: "Give learners meaning before asking for production.",
      mainTakeawayTitle: "Meaning, then form",
      noteBody:
        "Add supporting context that does not carry a warning or focus.",
      noteTitle: "Planning note",
      teacherFocusBody:
        "Notice what learners understand before correcting form.",
      teacherFocusTitle: "Watch the learner",
    },
  },
  navigation: {
    levels: "Study Library",
    system: "Система Interclass",
  },
  actions: {
    useFirst: "Use first",
    thenChoose: "Then choose",
  },
  ui: {
    loading: "Завантаження матеріалів",
    cancel: "Cancel",
    close: "Закрити",
    sidebar: {
      title: "Навігація",
      description: "Основні розділи платформи.",
      toggle: "Показати або сховати навігацію",
    },
  },
  levels: {
    breadcrumbLabel: "Навігаційний шлях рівня",
    demonstrationLabel: "Демо",
    levelSupportDescription:
      "Explore level-wide ideas and routines to use alongside every topic.",
    levelSupportTitle: (levelTitle: string) => `Throughout ${levelTitle}`,
    openLevelSupport: "Open level support",
    openTeachingGuide: "Open teaching guide",
    pageTitle: "Study Library",
    teachingGuideDescription:
      "Review the Interclass system before planning topics and activities.",
    teachingGuideTitle: (levelTitle: string) =>
      `Teaching with Interclass at ${levelTitle}`,
    topicsHeading: "Topics",
  },
  library: {
    aboutSystem: "Як працює система Interclass?",
    description: "Послідовна програма та розвиток тем",
    viewsLabel: "Подання Study Library",
    byLevel: "Рівні",
    byTopic: "Теми",
    grammar: "Граматика",
    viewCaptions: {
      level: "Послідовний шлях навчання",
      topic: "Розвиток тем крізь рівні",
      grammar: "Конструкції та їх розвиток",
    },
    chooseLevel: "Оберіть рівень",
    back: "До Study Library",
    themeCatalogue: "Оберіть тему",
    openTopic: "Відкрити тему",
    topicNumber: (number: number) => `Тема ${String(number).padStart(2, "0")}`,
    levelsLabel: "Рівні",
  },
  curriculum: {
    themeNames: {
      PEOPLE: "People, Family and Relationships",
      HOME: "Daily Life and Home",
      SCHOOL: "School and Education",
      LEISURE: "Leisure, Sports, Arts and Media",
      HEALTH: "Food, Body and Health",
      CITY: "City, Transport and Jobs",
      NATURE: "Nature, Weather and Environment",
      TRAVEL: "Travel, Countries, Holidays and Culture",
    },
    grammarGroups: {
      "Основи речення": "Sentence Basics",
      Займенники: "Pronouns",
      "Іменники та означники": "Nouns and Determiners",
      "Часи: Present": "Present Tenses",
      "Часи: Past": "Past Tenses",
      "Часи: Future": "Future Tenses",
      Модальність: "Modality",
      "Прикметники та прислівники": "Adjectives and Adverbs",
      "Лексична граматика": "Lexical Grammar",
      Синтаксис: "Syntax",
      "Дієслівні конструкції": "Verb Patterns",
      "Стан і трансформація": "Voice and Transformations",
      Умовність: "Conditionals",
      "Передавання мовлення": "Reported Speech",
    },
    grammarNames: {
      G01: "Verb to be",
      G02: "Have got (possession)",
      G03: "Personal pronouns",
      G04: "Object pronouns",
      G05: "Possessive pronouns and possessive 's",
      G06: "Reflexive pronouns",
      G07: "Singular and plural nouns",
      G08: "Articles: a, an and the",
      G09: "Countable and uncountable nouns",
      G10: "Quantifiers",
      G11: "There is and there are",
      G12: "Cardinal and ordinal numbers; telling the time",
      G13: "Present Simple",
      G14: "Present Continuous",
      G15: "Present Perfect",
      G16: "Present Perfect Continuous",
      G17: "Past Simple",
      G18: "Irregular verbs",
      G19: "Past Continuous",
      G20: "Past Perfect",
      G21: "Past Perfect Continuous",
      G22: "Narrative tenses",
      G23: "Future Simple (will)",
      G24: "Future with be going to",
      G25: "Future forms",
      G26: "Can and could",
      G27: "Must, have to, need and should",
      G28: "Modal verbs and modality",
      G29: "Requests and suggestions",
      G30: "Adjective order and descriptive adjectives",
      G31: "Comparatives and superlatives",
      G32: "Adverbs and adverbs of frequency",
      G33: "Word formation",
      G34: "Compound nouns (noun + noun)",
      G35: "Phrasal verbs",
      G36: "Prepositions of place and movement",
      G37: "Questions and question tags",
      G38: "Complex sentences and relative clauses",
      G39: "Linking words and expressions of comparison",
      G40: "Gerunds and infinitives",
      G41: "Past participles (V3)",
      G42: "Passive voice",
      G43: "Have something done (causative)",
      G44: "Zero and first conditionals",
      G45: "Second and third conditionals",
      G46: "Used to, would and be used to",
      G47: "Wish",
      G48: "Reported speech",
      G49: "Sequence of tenses",
      G50: "Sentence transformations",
      G51: "Make and do collocations",
      G52: "Both, either and neither",
      G53: "Present tenses (general)",
      G54: "Past tenses (general)",
      G55: "Conditionals (general)",
    },
    programme: "Програма рівня",
    catalogue: "Навчальні підрозділи",
    materials: "Методичні матеріали",
    materialsHelp:
      "Теми з методичними рекомендаціями та навчальними матеріалами.",
    openLevel: "Переглянути рівень",
    browseTopics: "Переглянути теми",
    plannedTopics: "Рівень заплановано.",
    openSection: "Відкрити підрозділ",
    openMaterials: "Матеріали теми",
    unavailable: "Матеріали готуються",
    foundation: "Основи",
    themeLevels: "Тема в різних рівнях",
    themeHelp:
      "Оберіть напрям і розгорніть рівень, щоб побачити всі пов’язані теми.",
    grammarSearch: "Пошук граматичної конструкції",
    grammarPlaceholder: "Наприклад, Present Perfect",
    search: "Знайти",
    clearSearch: "Очистити",
    noGrammarResults: "За цим запитом конструкцій не знайдено.",
    chooseGrammar: "Оберіть граматичну конструкцію.",
    grammarLevels: "Де опрацьовується конструкція",
    grammarSource: "Граматика в джерелі",
    unitNote:
      "Посилання веде до матеріалів навчальної теми, що охоплює цей підрозділ.",
    sectionTitle: "Підрозділ програми",
    themeContext: "Тематичний контекст",
    secondaryTheme: "Додаткова тема",
    grammarContext: "Граматичні конструкції",
    linkedSections: "Пов’язані підрозділи програми",
    chapters: "Навчальні розділи",
    browseChapters: "Переглянути розділи",
    openChapter: "Відкрити розділ",
    collapseChapter: "Згорнути розділ",
    chapterSections: "Підрозділи",
    chapterNavigation: "Підрозділи розділу",
    resources: "Додаткові матеріали",
    resourceHelp: "Посилання на файли ще не додано.",
    examPractice: "Екзаменаційна практика",
    examBadge: (exam: string) => `Готує до ${exam}`,
    sectionsCount: curriculumTopicCount,
    levelsCount: (count: number) => `Рівнів: ${count}`,
  },
  guides: {
    backToLevel: (levelTitle: string) => `Back to ${levelTitle}`,
    emptyDescription:
      "This level's teaching guide is being prepared. You can still explore its topics.",
    emptyTitle: "Teaching guide coming soon",
    levelSearchLabel: (levelTitle: string) =>
      `Search ${levelTitle} teaching guide concepts`,
    levelSearchPlaceholder: "Search teaching guide concepts...",
    levelSubtitle: "A quick reference to the ideas behind teaching this level.",
    levelTitle: (levelTitle: string) => `Teaching guide: ${levelTitle}`,
  },
  levelSupport: {
    emptyDescription:
      "Support for this level is being prepared. You can still explore its topics.",
    emptyTitle: "Level support coming soon",
    searchLabel: (levelTitle: string) => `Search support for ${levelTitle}`,
    searchPlaceholder: "Search level support concepts...",
    subtitle: "Level-wide ideas and routines to use alongside every topic.",
    title: (levelTitle: string) => `Throughout ${levelTitle}`,
  },
  materialCategories: {
    ...editorFeedback,
    title: "Material categories",
    help: "Manage categories shared across all topics. Changes to a category apply everywhere it is used.",
    topicTitle: "Materials for this topic",
    readerHelp: "Choose a resource to use at any stage of your lesson.",
    empty: "Materials are being prepared.",
    add: "Create category",
    newTitle: "New category",
    name: "Category name",
    color: "Main color",
    colorPicker: (label: string) => `${label} picker`,
    accentColor: "Accent color",
    colorHelp:
      "Main color is used for the icon and border; accent color sets the pale background.",
    defaultDescription: "Default material description",
    defaultDescriptionHelp:
      "Used wherever this category is selected, unless a material has its own description.",
    icon: "Category icon",
    iconHelp:
      "Upload a PNG, JPEG, or WebP icon. It will appear in a square alongside the category name.",
    preset:
      "Upload an image to replace the built-in icon. Removing the image restores its preset icon.",
    save: "Save category",
    cancel: "Cancel",
    linkUrl: "Material URL",
    description: "Material description",
    descriptionHelp: "Leave blank to use the category default.",
    resourceTitle: "Resource title (optional)",
    previewUrl: "Preview URL (optional)",
    previewUrlHelp:
      "Use a provider embed URL or a direct PDF/image URL. Folder links have no individual preview.",
    usageInstructions: "How to use this material (optional)",
    usageInstructionsHelp:
      "Add reviewed resource-specific guidance. Do not infer contents or a teaching stage from the category.",
    learningPurpose: "Learning purpose (optional)",
    teachingStage: "Teaching stage (optional)",
    noTeachingStage: "No stage assignment",
    downloadUrl: "Download URL (optional)",
    downloadUrlHelp:
      "Add only a verified, permitted download link. Leave blank to hide downloading.",
    category: "Category",
    chooseCategory: "Choose a category",
    addMaterial: "Add material",
    removeMaterial: "Remove material",
    noCategories:
      "Create a category in Material categories to start adding materials.",
    duplicate: "A category with this ID already exists.",
    identity: "A saved category's ID and kind cannot be changed.",
    invalidIcon: "Upload an icon to the configured image store before saving.",
    missingCategory:
      "A selected material category does not exist. Reload the editor and select an existing category.",
  },
  contentEditor: {
    ...editorFeedback,
    newMaterialTitle: "New material",
    duplicateTopic: "A topic with this ID already exists.",
    title: "Content Lab",
    subtitle:
      "Choose a scope, then edit its curriculum topics or teaching guide.",
    createTopic: "Create topic",
    createNewTopic: "Create new topic",
    createTopicDialogTitle: "Create new topic",
    createTopicDialogDescription:
      "Add a topic name and URL slug for the selected level. The topic starts as a structured skeleton you can edit after creation.",
    newTopicName: "Topic name",
    newTopicSlug: "New topic slug",
    saveDraft: "Save",
    saving: "Saving",
    viewTopic: "View topic",
    unsavedChanges: "Unsaved changes",
    unsavedChangesConfirm:
      "You have unsaved changes. Leave this topic and discard them?",
    noTopicSelected: "Choose or create a topic.",
    createTopicError: "Add a topic name and a unique kebab-case slug.",
    createTopicNamePlaceholder: "Family",
    createTopicPlaceholder: "family",
    deleteTopic: "Delete topic",
    deletingTopic: "Deleting topic",
    deleteTopicDialogTitle: "Delete topic?",
    deleteTopicDialogDescription: (topicTitle: string, levelTitle: string) =>
      `Delete “${topicTitle}” from ${levelTitle}? Its saved content and any unsaved changes will be permanently removed.`,
    missingLevel: "The selected level does not exist.",
    missingTopic: "The selected topic does not exist.",
    topicList: "Topics",
    sortTopicsByTitle: "Sort A–Z",
    sortTopicsByTitleLabel: "Sort topics by title",
    sortTopicsConfirm:
      "Sort all topics in this level by title? Numbers sort naturally (Lesson 2 before Lesson 10). This replaces the current order.",
    topicOrderSaved: "Topic order saved.",
    topicOrderConflict:
      "Topics in this level changed in another tab or session. Reload the page to review the latest order before reordering again.",
    topicDetailsSection: "Details",
    moreTopicDetails: "Intro video, theory, and links",
    stagesSection: "Stages",
    stagesHelp: "Drag to reorder. Click a title to rename it.",
    addStage: "Add stage",
    removeStage: (stageNumber: number) => `Remove stage ${stageNumber}`,
    removeStageConfirm:
      "Remove this stage and its teaching text? The change applies when you save.",
    toggleStage: (stageNumber: number) => `Show stage ${stageNumber} text`,
    stageTitle: (stageNumber: number) => `Stage ${stageNumber} title`,
    stageColor: "Stage color",
    stageColorButton: (stageNumber: number) => `Stage ${stageNumber} color`,
    stageColorHelp:
      "New stages get a default color. With light colors, the title uses dark text to stay readable.",
    emptyStage: "No text yet",
    untitledStage: (stageNumber: number) => `Stage ${stageNumber}`,
    materialsHelp: "Shown below the stages, in this order.",
    toggleMaterial: (title: string) => `Show ${title} details`,
    sorting: {
      handle: (label: string) => `Reorder ${label}`,
      instructions:
        "To reorder, press Space or Enter, move with the arrow keys, then press Space or Enter to drop. Press Escape to cancel.",
      pickedUp: (label: string) => `Picked up ${label}.`,
      movedTo: (label: string, position: number, total: number) =>
        `${label} moved to position ${position} of ${total}.`,
      droppedAt: (label: string, position: number, total: number) =>
        `${label} dropped at position ${position} of ${total}.`,
      cancelled: (label: string) =>
        `Reordering cancelled. ${label} returned to its place.`,
    },
    addMaterial: "Add material",
    removeItem: "Remove item",
    materialCount: "materials",
    materialActions: "Material actions",
    editMaterial: "Edit material",
    materialSheetDescription: "Edit title, links, tags, and teaching notes.",
    noMaterials: "No materials yet.",
    useNewLines: "Use one item per line.",
    scopePickerLabel: "Content scope",
    levelModePickerLabel: "Level content type",
    scopes: {
      system: "System",
    },
    modes: {
      curriculum: "Curriculum",
      guide: "Guide",
      support: "Support",
    },
    discardDialog: {
      title: "Discard unsaved changes?",
      description:
        "Switching scope or content type will discard the changes in this draft.",
      keepEditing: "Keep editing",
      discard: "Discard and switch",
    },
    pools: {
      useFirst: "Use first materials",
      thenChoose: "Then choose materials",
    },
    sections: {
      teachingNote: "Teaching note",
      materials: "Materials",
    },
    fields: {
      description: "Description",
      diagnostic: "Diagnostic",
      duration: "Duration",
      examplePhrases: "Example phrases",
      expectedResults: "Expected results",
      goal: "Goal",
      howToUse: "How to use",
      id: "ID",
      introVideoUrl: "Intro video URL",
      lessonVideoUrl: "Lesson video URL",
      nextTopicId: "Next topic ID",
      previousTopicId: "Previous topic ID",
      purpose: "Purpose",
      stageType: "Stage type",
      steps: "Steps",
      summary: "Summary",
      tags: "Tags",
      teacherTip: "Teacher tip",
      theory: "Theory",
      thumbnail: "Thumbnail",
      title: "Title",
      type: "Type",
      url: "URL",
    },
  },
  systemOverview: {
    title: "Система Interclass",
    back: "До короткого огляду",
    curriculum: "Переглянути програму",
    curriculumDescription: "Рівні, теми та підготовка до іспитів",
    details: "Докладно про систему",
    detailsDescription: "Принципи та методика Interclass",
    nextStep: "Оберіть наступний крок",
  },
  system: {
    title: "System",
    subtitle: "A quick reference to the ideas behind the method.",
    conceptLabel: "Concept",
    searchLabel: "Search System concepts",
    searchPlaceholder: "Search System concepts...",
    searchResults: "Search results",
    noSearchResults: "No matching concepts found.",
    contentComing: "Content is being prepared.",
    contentComingDescription:
      "This concept is ready to be completed in Content Lab.",
    previousConcept: "Previous",
    nextConcept: "Next",
    pagination: "Concept pagination",
    mainIdea: "Main idea",
    howItWorks: "How it works",
    video: "Video",
    openVideo: "Open video on YouTube",
    playVideo: "Play video",
    readDeeper: "Read deeper",
    openResource: "Open resource",
    guidanceKinds: {
      teacherFocus: "Teacher focus",
      commonMistake: "Common mistake",
      mainTakeaway: "Main takeaway",
      note: "Note",
    },
  },
  systemEditor: {
    ...editorFeedback,
    title: "Guide content",
    subtitle: "Edit the concepts shown in this teaching guide.",
    supportTitle: "Level Support content",
    supportSubtitle: "Edit the concepts shown throughout this level.",
    concepts: "Concepts",
    addConcept: "Add concept",
    createConceptTitle: "New concept",
    createConceptDescription:
      "Add a title and a URL slug. The concept starts empty; fill it in after creating.",
    newConceptTitle: "Concept title",
    newConceptSlug: "URL slug",
    newConceptTitlePlaceholder: "Learning cycles",
    newConceptSlugPlaceholder: "learning-cycles",
    createConcept: "Create concept",
    creating: "Creating",
    duplicateConcept: "A concept with that slug already exists.",
    deleteConcept: "Delete concept",
    deleteConceptConfirm: "Delete this concept? This cannot be undone.",
    minimumSystemConcept: "The System guide must keep at least one concept.",
    emptyTitle: "No guide concepts yet",
    emptyDescription:
      "Add the first concept to start building this teaching guide.",
    emptySupportTitle: "No Level Support concepts yet",
    emptySupportDescription:
      "Add the first concept to start building support for this level.",
    conceptDetails: "Concept details",
    mainIdea: "Main idea",
    howItWorks: "How it works",
    guidance: "Teacher guidance",
    video: "Video",
    furtherReading: "Read deeper",
    save: "Save",
    saving: "Saving",
    viewConcept: "View concept",
    unsavedChanges: "Unsaved changes",
    unsavedChangesConfirm:
      "You have unsaved changes. Leave this concept and discard them?",
    missingGuide: "The selected guide does not exist.",
    missingSupport: "The selected Level Support collection does not exist.",
    missingConcept: "The selected guide concept does not exist.",
    missingSupportConcept: "The selected Level Support concept does not exist.",
    useNewLines: "Use one paragraph or item per line.",
    markdownHelp:
      "Use paragraphs, bold, italic, links, bulleted lists, and numbered lists.",
    toggleSection: "Toggle section",
    addSection: "Add section",
    addGuidance: "Add guidance",
    addReading: "Add resource",
    addVideo: "Add video",
    removeVideo: "Remove video",
    removeItem: "Remove item",
    moveUp: "Move up",
    moveDown: "Move down",
    formatting: {
      bold: "Bold",
      edit: "Edit",
      italic: "Italic",
      preview: "Preview",
    },
    markdownEditor: {
      editable: "Editable Markdown",
      undo: (shortcut: string) => `Undo ${shortcut}`,
      redo: (shortcut: string) => `Redo ${shortcut}`,
      bold: "Bold",
      removeBold: "Remove bold",
      italic: "Italic",
      removeItalic: "Remove italic",
      bulletedList: "Bulleted list",
      numberedList: "Numbered list",
      createLink: "Add link",
      linkUrl: "URL",
      linkUrlPlaceholder: "Paste a link URL",
      linkText: "Link text",
      linkTextHelp: "The text displayed for the link",
      setLink: "Set link",
      cancelLink: "Cancel link changes",
      save: "Save",
      cancel: "Cancel",
      openLink: (url: string) => `Open ${url} in a new window`,
      editLink: "Edit link URL",
      copyLink: "Copy link URL",
      linkCopied: "Copied",
      removeLink: "Remove link",
    },
    image: {
      detached: "Image will be removed when you save.",
      emptyFile: "Choose an image to upload.",
      fileHelp: "JPEG, PNG or WebP, up to 4 MB.",
      fullWidth: "Full width",
      invalidFile: "Choose a JPEG, PNG or WebP image up to 4 MB.",
      invalidSource: "Images must come from the configured System image store.",
      naturalHeight: "Automatic height",
      notConfigured: "Image uploads are not configured in this environment.",
      remove: "Remove image",
      replace: "Replace image",
      upload: "Upload image",
      uploadFailed: "The image could not be uploaded. Try again.",
      uploading: "Uploading image",
    },
    guidanceKinds: {
      label: "Guidance type",
      teacherFocus: "Teacher focus",
      commonMistake: "Common mistake",
      mainTakeaway: "Main takeaway",
      note: "Note",
    },
    fields: {
      imageMaxHeight: "Maximum height (px)",
      imageMaxWidth: "Maximum width (px)",
      title: "Title",
      subtitle: "Short subtitle",
      sectionTitle: "Section title",
      sectionBody: "Section content",
      videoTitle: "Video title",
      videoDescription: "Video description",
      videoUrl: "Video URL",
      guidanceTitle: "Guidance title",
      guidanceBody: "Guidance text",
      resourceTitle: "Resource title",
      resourceUrl: "Resource URL",
    },
    defaults: {
      sectionTitle: "New section",
      paragraph: "New paragraph",
      guidanceTitle: "New guidance",
      guidanceBody: "New guidance note",
      resourceTitle: "New resource",
    },
  },
  resourcePreview: {
    preview: "Переглянути матеріал",
    dialogDescription: "Перегляд матеріалу та рекомендації для вчителя.",
    open: "Відкрити матеріал",
    download: "Завантажити",
    close: "Закрити",
    guidance: "Як працювати з матеріалом",
    noGuidance: "Рекомендації до цього матеріалу ще не додано.",
    purpose: "Навчальна мета",
    stage: "Етап Interclass",
    loading: "Завантаження перегляду…",
    unavailable: "Перегляд недоступний. Відкрийте матеріал на сайті джерела.",
    collection:
      "Це добірка матеріалів. Відкрийте її на сайті джерела, щоб обрати потрібний файл або вправу.",
    accessHelp:
      "Якщо матеріал не відображається або потрібен вхід, відкрийте його на сайті джерела. Налаштування доступу залишаються без змін.",
    notVisible: "Не відображається?",
    retry: "Спробувати ще раз",
    stageMaterials: "Матеріали до етапу",
  },
  topicWorkspace: {
    breadcrumbLabel: "Навігаційний шлях теми",
    unitContents: "Уроки теми",
    expandContents: "Показати всі підтеми",
    collapseContents: "Згорнути",
    openStory: "Перейти до історії",
    unassignedGuidance: "Рекомендації до теми",
    unassignedGuidanceHelp:
      "Послідовність рекомендацій збережено. Їхній розподіл між чотирма етапами ще не погоджено.",
    teachingCycle: "Навчальний цикл",
    stageMaterialsEmpty: "Для цього етапу матеріалів ще не додано.",
    grammarFocus: "Grammar Focus",
    learningGoals: "Learning Goals",
    storytellingFocus: "Storytelling Focus",
    storytellingDetails: {
      skills: "Навички",
      related: "Пов’язані теми програми",
    },
    additionalGuidance: "Додаткові рекомендації",
    unitPreparation: "Підготовка до теми",
    existingGuidance: "Додаткові рекомендації до етапу",
    editorialStage: {
      adaptation: "Методична адаптація Interclass",
      goal: "Мета",
      instructions: "Як працюємо",
      practice: "Практика",
      outcome: "Очікуваний результат",
    },
    materials: "Навчальні матеріали",
    noMaterials: "Матеріали готуються.",
    openResource: "Відкрити",
    teachingStages: "Як працювати з темою",
    chooseStage: "Оберіть етап, щоб побачити методичну рекомендацію.",
    stage: {
      howToTeach: "Як працювати на цьому етапі",
      emptyBody: "Методичні рекомендації для цього етапу ще не додані.",
    },
    progression: "Навчальна наступність",
    previousLevels: "Попередні рівні",
    nextLevels: "Наступні рівні",
    sameLevel: "На цьому рівні",
    themeLevels: "Теми на інших рівнях",
    levelPath: "Рівні пов’язаних тем",
    relatedGrammar: "Пов’язана граматика",
    guidance: "Методичний супровід",
    aboutTopic: "Про тему",
    relations: {
      revision: "Повторення",
      progression: "Розвиток теми",
      prerequisite: "Попередня підготовка",
    },
  },
  topic: {
    breadcrumbLabel: "Breadcrumb",
    navigation: {
      previousTopic: "Previous topic",
      nextTopic: "Next topic",
    },
    stage: {
      howToTeach: "How to teach this stage",
      emptyBody: "Teaching guidance is being prepared.",
      goal: "Goal",
      teacherTip: "Teacher tip",
      howToUse: "How to use this stage",
      expectedResult: "Expected result",
      diagnostic: "Check before moving on",
      emptyMaterials: "Materials will be attached here.",
    },
  },
} as const;
